import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Camera, Check, QrCode, WifiHigh as Wifi } from "@phosphor-icons/react";

export const Info = ({ label, value, full }) => (
  <div className={`p-2 rounded-md bg-white/5 ${full ? "col-span-2" : ""}`}>
    <div className="text-[9px] uppercase tracking-widest text-white/40">{label}</div>
    <div className="font-mono text-sm">{value}</div>
  </div>
);

export const PhotoSlot = ({ label, url, onChange, testId }) => {
  const inputRef = React.useRef(null);
  const fullUrl = url ? `${process.env.REACT_APP_BACKEND_URL}${url}` : null;
  return (
    <div className="flex items-center gap-3 bg-white/5 rounded-lg p-2">
      <div className="w-16 h-16 rounded-md overflow-hidden bg-white/10 flex items-center justify-center flex-shrink-0">
        {fullUrl ? <img src={fullUrl} alt={label} className="w-full h-full object-cover" /> : <Camera size={20} weight="duotone" className="text-white/40" />}
      </div>
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{label}</div>
        <button onClick={() => inputRef.current?.click()} className="text-xs text-white hover:underline mt-0.5" data-testid={testId}>
          {url ? "Neu aufnehmen" : "Foto aufnehmen"}
        </button>
        <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { onChange(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
};

export const BinScanSlot = ({ jobId, bins, scannedBins, onScanned }) => {
  const [scanning, setScanning] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manual, setManual] = useState("");
  const [feedback, setFeedback] = useState(null);
  const scannedIds = new Set((scannedBins || []).map(s => s.bin_id));

  const doScan = async (payload) => {
    try {
      const r = await api.post("/driver/scan/bin", { job_id: jobId, ...payload });
      if (r.data.matched) {
        setFeedback({ ok: true, msg: `Tonne ${r.data.bin?.label || r.data.bin?.bin_type} verifiziert.` });
        if (navigator.vibrate) navigator.vibrate([80, 40, 80]);
        onScanned && onScanned();
      } else {
        setFeedback({ ok: false, msg: "Tonne nicht erkannt." });
        if (navigator.vibrate) navigator.vibrate(300);
      }
    } catch (e) {
      setFeedback({ ok: false, msg: e.response?.data?.detail || "Scan-Fehler" });
    }
    setTimeout(() => setFeedback(null), 3000);
  };

  const nfcScan = async () => {
    if (typeof window === "undefined" || !("NDEFReader" in window)) {
      alert("Web NFC wird auf diesem Gerät nicht unterstützt. Bitte Barcode/QR nutzen.");
      return;
    }
    try {
      setScanning(true);
      const reader = new window.NDEFReader();
      await reader.scan();
      reader.onreading = (ev) => {
        const uid = ev.serialNumber || "";
        setScanning(false);
        if (uid) doScan({ nfc_uid: uid });
      };
    } catch (e) {
      setScanning(false);
      alert("NFC-Scan fehlgeschlagen: " + e.message);
    }
  };

  const submitManual = async () => {
    if (!manual.trim()) return;
    await doScan(manual.trim().startsWith("nfc:") ? { nfc_uid: manual.replace(/^nfc:/, "").trim() } : { barcode: manual.trim() });
    setManual(""); setManualOpen(false);
  };

  return (
    <div className="bg-white/5 rounded-lg p-3 border border-white/10" data-testid={`bin-scan-${jobId}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Tonnen scannen</div>
        <div className="text-[10px] text-white/60">{scannedIds.size}/{bins.length}</div>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-2">
        {bins.map(b => (
          <div key={b.bin_id} className={`px-2 py-1 rounded text-[11px] flex items-center gap-1 ${scannedIds.has(b.bin_id) ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/60"}`} data-testid={`bin-slot-${b.bin_id}`}>
            {scannedIds.has(b.bin_id) ? <Check size={11} weight="bold" /> : <span className="w-2 h-2 rounded-full bg-white/20" />}
            <span className="truncate">{b.label || `${b.bin_type} ${b.size_liter}L`}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <button onClick={nfcScan} disabled={scanning} className="flex-1 py-2 rounded-full text-xs font-bold bg-white/10 hover:bg-white/15 flex items-center justify-center gap-1.5" data-testid={`nfc-btn-${jobId}`}>
          <Wifi size={13} weight="bold" /> {scanning ? "NFC…" : "NFC"}
        </button>
        <button onClick={() => setManualOpen(v => !v)} className="flex-1 py-2 rounded-full text-xs font-bold bg-white/10 hover:bg-white/15 flex items-center justify-center gap-1.5" data-testid={`barcode-btn-${jobId}`}>
          <QrCode size={13} weight="bold" /> Barcode
        </button>
      </div>
      {manualOpen && (
        <div className="mt-2 flex gap-2">
          <input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Barcode oder nfc:UID" className="flex-1 px-2 py-1.5 rounded text-xs bg-white/10 border-white/10 text-white" data-testid={`manual-input-${jobId}`} />
          <button onClick={submitManual} className="px-3 rounded text-xs bg-white text-[#0e1a23] font-bold" data-testid={`manual-submit-${jobId}`}>OK</button>
        </div>
      )}
      {feedback && (
        <div className={`mt-2 text-xs px-2 py-1 rounded ${feedback.ok ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300"}`} data-testid={`scan-feedback-${jobId}`}>
          {feedback.msg}
        </div>
      )}
    </div>
  );
};


export const SkipSlot = ({ jobId, onDone }) => {
  const [open, setOpen] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [reasonCode, setReasonCode] = useState("");
  const [notes, setNotes] = useState("");
  const [photoUrl, setPhotoUrl] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputRef = React.useRef(null);

  useEffect(() => {
    if (open && reasons.length === 0) {
      api.get("/skip-reasons").then(r => setReasons(r.data || [])).catch(() => {});
    }
  }, [open, reasons.length]);

  const takePhoto = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/photo?kind=skip`, {
        method: "POST", body: fd, credentials: "include",
        headers: localStorage.getItem("tonneva_token") && localStorage.getItem("tonneva_token") !== "__cookie__"
          ? { Authorization: `Bearer ${localStorage.getItem("tonneva_token")}` } : {},
      });
      const data = await r.json();
      if (data.photo_url) setPhotoUrl(data.photo_url);
    } catch (e) { alert("Foto-Upload fehlgeschlagen: " + e.message); }
  };

  const submit = async () => {
    if (!reasonCode) return;
    setSaving(true);
    try {
      await api.post(`/jobs/${jobId}/skip`, { reason_code: reasonCode, notes, photo_url: photoUrl });
      setOpen(false);
      setReasonCode(""); setNotes(""); setPhotoUrl(null);
      onDone && onDone();
    } catch (e) { alert(e.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="w-full text-xs text-white/60 hover:text-white py-2 underline" data-testid={`driver-skip-${jobId}`}>
        Reinigung kann nicht durchgeführt werden
      </button>
    );
  }
  const fullUrl = photoUrl ? `${process.env.REACT_APP_BACKEND_URL}${photoUrl}` : null;
  return (
    <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 space-y-2" data-testid={`skip-slot-${jobId}`}>
      <div className="text-[10px] uppercase tracking-widest text-red-300 font-bold">Reinigung nicht möglich</div>
      <div>
        <div className="text-[10px] text-white/60 mb-1">Grund wählen</div>
        <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)} className="w-full text-xs px-2 py-2 rounded bg-white/10 border border-white/10 text-white" data-testid={`skip-reason-${jobId}`}>
          <option value="">— bitte wählen —</option>
          {reasons.map(r => <option key={r.reason_id} value={r.code}>{r.label}</option>)}
        </select>
      </div>
      <div>
        <div className="text-[10px] text-white/60 mb-1">Notiz (optional)</div>
        <input value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full text-xs px-2 py-2 rounded bg-white/10 border border-white/10 text-white" placeholder="Details…" data-testid={`skip-notes-${jobId}`} />
      </div>
      <div className="flex items-center gap-2 bg-white/5 rounded-lg p-2">
        <div className="w-12 h-12 rounded-md overflow-hidden bg-white/10 flex items-center justify-center flex-shrink-0">
          {fullUrl ? <img src={fullUrl} alt="skip" className="w-full h-full object-cover" /> : <Camera size={16} weight="duotone" className="text-white/40" />}
        </div>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">Beweis-Foto (optional)</div>
          <button onClick={() => inputRef.current?.click()} className="text-xs text-white hover:underline mt-0.5" data-testid={`skip-photo-btn-${jobId}`}>
            {photoUrl ? "Neu aufnehmen" : "Foto aufnehmen"}
          </button>
          <input ref={inputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { takePhoto(e.target.files?.[0]); e.target.value = ""; }} />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <button onClick={() => { setOpen(false); setReasonCode(""); setNotes(""); setPhotoUrl(null); }} className="flex-1 py-2 rounded-full text-xs font-bold bg-white/10 hover:bg-white/15" data-testid={`skip-cancel-${jobId}`}>Abbrechen</button>
        <button onClick={submit} disabled={!reasonCode || saving} className="flex-1 py-2 rounded-full text-xs font-bold bg-red-500 text-white hover:bg-red-600 disabled:opacity-50" data-testid={`skip-confirm-${jobId}`}>
          {saving ? "Sende…" : "Bestätigen"}
        </button>
      </div>
    </div>
  );
};



/**
 * HUB-Bestätigung durch Fahrer (Versicherung).
 * Bei Kunden mit gebuchtem HUB muss der Fahrer vor Ort bestätigen: geleistet ja/nein.
 */
export function HubConfirmSlot({ job, onDone }) {
  const [busy, setBusy] = useState(false);
  const [actual, setActual] = useState(job.customer_hub_distance || 0);
  const [confirmed, setConfirmed] = useState(job.hub_confirmed_by_driver);
  const [note, setNote] = useState("");
  const eur = (c) => `€${((c || 0) / 100).toFixed(2)}`;

  const submit = async (yes) => {
    setBusy(true);
    try {
      await api.post(`/jobs/${job.job_id}/hub-confirm`, {
        confirmed: yes,
        distance_meters: yes ? actual : null,
        note: note || null,
      });
      setConfirmed(yes);
      onDone?.();
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl p-3 bg-amber-500/10 border border-amber-400/40 space-y-2" data-testid={`hub-slot-${job.job_id}`}>
      <div className="flex items-center justify-between text-xs uppercase tracking-widest font-bold text-amber-200">
        <span>HUB — Kunde hat gebucht</span>
        <span className="font-mono">{eur(job.customer_hub_price_cents)}</span>
      </div>
      <div className="text-xs text-white/80">Distanz (gebucht): <strong>{job.customer_hub_distance} m</strong></div>
      {confirmed === true && (
        <div className="text-xs text-emerald-300 font-bold" data-testid={`hub-yes-${job.job_id}`}>✓ Bestätigt — {job.hub_actual_meters || actual} m</div>
      )}
      {confirmed === false && (
        <div className="text-xs text-red-300 font-bold" data-testid={`hub-no-${job.job_id}`}>✗ Nicht geleistet — HUB wird nicht berechnet</div>
      )}
      {(confirmed === null || confirmed === undefined) && (
        <>
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase text-white/60">Tatsächlich (m)</label>
            <input
              type="number"
              min={0}
              value={actual}
              onChange={(e) => setActual(parseInt(e.target.value) || 0)}
              className="h-8 w-20 rounded-md bg-white/10 text-white px-2 text-sm border border-white/20"
              data-testid={`hub-actual-input-${job.job_id}`}
            />
          </div>
          <input
            type="text"
            placeholder="Notiz (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="h-8 w-full rounded-md bg-white/10 text-white px-2 text-sm border border-white/20"
            data-testid={`hub-note-input-${job.job_id}`}
          />
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => submit(true)}
              disabled={busy}
              className="rounded-full py-2 text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-60"
              data-testid={`hub-confirm-yes-${job.job_id}`}
            >
              ✓ HUB geleistet
            </button>
            <button
              onClick={() => submit(false)}
              disabled={busy}
              className="rounded-full py-2 text-xs font-bold bg-white/10 text-white hover:bg-white/20 disabled:opacity-60 border border-white/20"
              data-testid={`hub-confirm-no-${job.job_id}`}
            >
              ✗ Nicht geleistet
            </button>
          </div>
        </>
      )}
    </div>
  );
}
